<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { useVuelidate } from '@vuelidate/core';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    selectedResponse: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      title: '',
      description: '',
      showOnSidebar: true,
      color: '',
      isPintar: false,
      pintarPriority: 1,
      isKanban: false,
      kanbanOrder: 1,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    pageTitle() {
      return `${this.$t('LABEL_MGMT.EDIT.TITLE')} - ${
        this.selectedResponse.title
      }`;
    },
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
  },
  mounted() {
    this.setFormValues();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    setFormValues() {
      this.title = this.selectedResponse.title;
      this.description = this.selectedResponse.description || '';
      this.showOnSidebar = this.selectedResponse.show_on_sidebar;
      this.color = this.selectedResponse.color;

      this.isPintar = this.description.includes('[pintar=true]');
      const matchPintar = this.description.match(/\[bg_priority:(\d+)\]/);
      this.pintarPriority = matchPintar ? parseInt(matchPintar[1], 10) : 1;

      this.isKanban = this.description.includes('[kanban=true]') || /\[kanban:\d+\]/.test(this.description);
      const matchKanban = this.description.match(/\[kanban:(\d+)\]/);
      this.kanbanOrder = matchKanban ? parseInt(matchKanban[1], 10) : 1;
    },
    generateFinalDescription() {
      let desc = this.description
        .replace(/\[pintar=true\]/g, '')
        .replace(/\[bg_priority:\d+\]/g, '')
        .replace(/\[kanban=true\]/g, '')
        .replace(/\[kanban:\d+\]/g, '')
        .trim();
      
      if (this.isPintar) {
        desc += ` [pintar=true] [bg_priority:${this.pintarPriority}]`;
      }
      if (this.isKanban) {
        desc += ` [kanban:${this.kanbanOrder}]`;
      }
      return desc.trim();
    },
    editLabel() {
      this.$store
        .dispatch('labels/update', {
          id: this.selectedResponse.id,
          color: this.color,
          description: this.generateFinalDescription(),
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
        })
        .then(() => {
          useAlert(this.$t('LABEL_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          setTimeout(() => this.onClose(), 10);
        })
        .catch(() => {
          useAlert(this.$t('LABEL_MGMT.EDIT.API.ERROR_MESSAGE'));
        });
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-wrap mx-0" @submit.prevent="editLabel">
      <woot-input
        v-model="title"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />
      <woot-input
        v-model="description"
        :class="{ error: v$.description.$error }"
        class="w-full"
        :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        @input="v$.description.$touch"
        @blur="v$.description.$touch"
      />
      <div class="w-full flex flex-col gap-3 mt-2 mb-4 bg-n-slate-2 p-3 rounded border border-n-slate-4">
        <h4 class="text-sm font-semibold text-n-slate-12 m-0">Configurações Especiais</h4>
        
        <!-- Kanban -->
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <input v-model="isKanban" type="checkbox" id="kanban_check_edit" />
            <label for="kanban_check_edit" class="m-0 text-sm font-medium">Ativar como Etapa do Kanban</label>
          </div>
          <div v-if="isKanban" class="flex items-center gap-2 ml-4">
            <label class="text-xs text-n-slate-11">Ordem da Coluna:</label>
            <input v-model.number="kanbanOrder" type="number" min="1" class="w-16 h-8 text-sm px-2 rounded border border-n-slate-3" />
          </div>
        </div>

        <!-- Pintar Fundo -->
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <input v-model="isPintar" type="checkbox" id="pintar_check_edit" />
            <label for="pintar_check_edit" class="m-0 text-sm font-medium">Colorir Fundo da Conversa</label>
          </div>
          <div v-if="isPintar" class="flex items-center gap-2 ml-4">
            <label class="text-xs text-n-slate-11">Prioridade da Cor:</label>
            <input v-model.number="pintarPriority" type="number" min="1" class="w-16 h-8 text-sm px-2 rounded border border-n-slate-3" />
          </div>
        </div>
      </div>
      <div class="w-full">
        <label>
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="color" />
        </label>
      </div>
      <div class="flex items-center w-full gap-2">
        <input v-model="showOnSidebar" type="checkbox" :value="true" />
        <label for="conversation_creation">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('LABEL_MGMT.FORM.EDIT')"
          :disabled="v$.title.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  :deep(input) {
    @apply lowercase;
  }
}
</style>
